// One shape for every composed screen: columns, rows — drawn once, exported three ways.
//
// A table is `{ columns: [{ key, label }], rows: [{ id, cells: { key: value } }] }`. The register,
// the journal, the counters and the family lists all come back in it, which is what lets one
// drawing routine and one set of exporters serve all of them. The exporters are pure text
// functions; the drawing and the download touch the DOM, and only when called.
//
// The exports are TEXT FILES the browser downloads through a Blob. Nothing is posted anywhere:
// the file is built in memory from the read model and handed to the person, which is the only
// direction data ever moves in this feature.

/** RFC 4180: a cell with a comma, a quote or a line break is quoted, quotes doubled. */
function csvCell(value) {
	const text = String(value === undefined || value === null ? '' : value);
	return /[",\r\n]/.test(text) ? '"' + text.replace(/"/g, '""') + '"' : text;
}

export function toCsv(table) {
	const lines = [table.columns.map((column) => csvCell(column.label)).join(',')];
	table.rows.forEach((row) => {
		lines.push(table.columns.map((column) => csvCell(row.cells[column.key])).join(','));
	});
	return lines.join('\r\n') + '\r\n';
}

/* A pipe table has no way to carry a pipe or a line break inside a cell; both become a space, and
   a multi-paragraph answer reads as one line — which is what a table format does to it anyway. */
function markdownCell(value) {
	return String(value === undefined || value === null ? '' : value)
		.replace(/\|/g, '\\|').replace(/\r?\n/g, ' ').trim();
}

export function toMarkdown(table) {
	const header = '| ' + table.columns.map((column) => markdownCell(column.label)).join(' | ') + ' |';
	const rule = '|' + table.columns.map(() => ' --- ').join('|') + '|';
	const body = table.rows.map((row) => '| '
		+ table.columns.map((column) => markdownCell(row.cells[column.key])).join(' | ') + ' |');
	return [header, rule].concat(body).join('\n') + '\n';
}

/** One object per row, keyed by column — the shape a script reading the file back would want. */
export function toJson(table) {
	const rows = table.rows.map((row) => {
		const object = {};
		table.columns.forEach((column) => { object[column.key] = row.cells[column.key] || ''; });
		return object;
	});
	return JSON.stringify(rows, null, '\t') + '\n';
}

/**
 * Hands a text to the browser as a file to save.
 *
 * The link is appended before it is clicked — one that is not in the document does not reliably
 * start a download everywhere — and the object URL is revoked right after, or the blob outlives
 * the page.
 */
export function download(filename, text, type) {
	const url = URL.createObjectURL(new Blob([text], { type }));
	const link = document.createElement('a');
	link.href = url;
	link.download = filename;
	document.body.append(link);
	link.click();
	link.remove();
	URL.revokeObjectURL(url);
}

/* A file name from a title: lower case, dashes, nothing a file system argues with. */
export function fileStem(title) {
	return String(title || 'my-data').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
		.replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'my-data';
}

/**
 * The three export buttons of a table. Built as real buttons, labelled from the vocabulary, so the
 * row is reachable by keyboard and read as what it is.
 */
export function exportBar(table, stem, labels) {
	const bar = document.createElement('div');
	bar.className = 'ui-buttons mydata-export';
	const offers = [
		[labels.exportJson, '.json', 'application/json', toJson],
		[labels.exportCsv, '.csv', 'text/csv', toCsv],
		[labels.exportMarkdown, '.md', 'text/markdown', toMarkdown],
	];
	offers.forEach(([label, extension, type, render]) => {
		const button = document.createElement('button');
		button.className = 'ui-button variant-soft';
		button.type = 'button';
		button.textContent = label;
		button.addEventListener('click', () => download(stem + extension, render(table), type));
		bar.append(button);
	});
	return bar;
}

/* The date, in the reader's own language, from the one place that knows it: the document. A value
   that will not parse is shown as it is rather than as "Invalid Date". */
export function readableDate(iso) {
	const when = new Date(iso);
	if (Number.isNaN(when.getTime())) return iso;
	return when.toLocaleDateString(document.documentElement.lang || undefined,
		{ year: 'numeric', month: 'long', day: 'numeric' });
}

/**
 * The table, drawn. `textContent` throughout: every cell is somebody's own words coming back to
 * them, from a store anyone using this machine can write to.
 *
 * `decorate(cell, row, column)` lets a screen add to a cell — a control, a class — after its text
 * is set; `decorateRow(line, row)` does the same for the row. `actions(row)` adds a last column of
 * controls, OUTSIDE the table model: what is exported is the data, never the buttons. All optional.
 */
export function drawTable(table, options) {
	const settings = options || {};
	const wrapper = document.createElement('div');
	wrapper.className = 'mydata-table';
	const element = document.createElement('table');
	element.className = 'ui-table';

	const head = document.createElement('thead');
	const headRow = document.createElement('tr');
	table.columns.forEach((column) => {
		const cell = document.createElement('th');
		cell.scope = 'col';
		cell.textContent = column.label;
		headRow.append(cell);
	});
	if (settings.actions) {
		const cell = document.createElement('th');
		cell.scope = 'col';
		// Named for a screen reader, blank for the eye: the buttons say what they do.
		const name = document.createElement('span');
		name.className = 'ui-visually-hidden';
		name.textContent = settings.actionsLabel || '';
		cell.append(name);
		headRow.append(cell);
	}
	head.append(headRow);
	element.append(head);

	const body = document.createElement('tbody');
	table.rows.forEach((row) => {
		const line = document.createElement('tr');
		if (row.id) line.dataset.id = row.id;
		table.columns.forEach((column) => {
			const cell = document.createElement('td');
			cell.textContent = row.cells[column.key] || '';
			if (settings.decorate) settings.decorate(cell, row, column);
			line.append(cell);
		});
		if (settings.actions) {
			const cell = document.createElement('td');
			cell.className = 'mydata-table-actions';
			cell.append(settings.actions(row));
			line.append(cell);
		}
		if (settings.decorateRow) settings.decorateRow(line, row);
		body.append(line);
	});
	element.append(body);
	wrapper.append(element);
	return wrapper;
}
